import type { Client, ClientType, MaritalStatus } from "../types";

/**
 * Client-type-aware RFC/CURP validation shape (Almikia Epic E3, MEMORY.md).
 * Reused by the client create/edit form and by TASK-025's CSV importer, so
 * every export here is a plain function with no react-admin/form coupling.
 *
 * This intentionally does not validate the embedded birth/incorporation date
 * or the homoclave checksum on the RFC, nor the CURP checksum — only the
 * length + character-set shape the ticket asks for (mirrors
 * `developers/isValidRfc.ts`, kept separate here because the expected RFC
 * length depends on `client_type`, which developers don't have).
 */

const RFC_LENGTH_BY_CLIENT_TYPE: Record<ClientType, number> = {
  persona_fisica: 13,
  persona_moral: 12,
};

const ALPHANUMERIC_REGEX = /^[A-Za-z0-9]+$/;
const CURP_LENGTH = 18;

/**
 * react-hook-form validator for a client's RFC. Optional; when present, must
 * be alphanumeric and match the length for the record's `client_type` (13 for
 * persona_fisica, 12 for persona_moral). Never echoes the entered value.
 */
export const validateClientRfc = (
  value: string | null | undefined,
  allValues: { client_type?: ClientType },
) => {
  if (!value) return undefined;
  const expectedLength =
    RFC_LENGTH_BY_CLIENT_TYPE[allValues.client_type ?? "persona_fisica"];
  if (value.length !== expectedLength || !ALPHANUMERIC_REGEX.test(value)) {
    return "crm.validation.invalid_rfc";
  }
  return undefined;
};

/**
 * react-hook-form validator for a client's CURP. Optional; rejected outright
 * for persona_moral clients (a legal entity has no CURP), and otherwise must
 * be 18 alphanumeric characters. Never echoes the entered value.
 */
export const validateClientCurp = (
  value: string | null | undefined,
  allValues: { client_type?: ClientType },
) => {
  if (!value) return undefined;
  if (allValues.client_type === "persona_moral") {
    return "crm.validation.curp_not_allowed_persona_moral";
  }
  if (value.length !== CURP_LENGTH || !ALPHANUMERIC_REGEX.test(value)) {
    return "crm.validation.invalid_curp";
  }
  return undefined;
};

/**
 * react-hook-form validator for a client's birthday. Optional; when present,
 * must not be in the future. Compares plain "YYYY-MM-DD" strings, so it is
 * not sensitive to time-of-day or timezone.
 */
export const validateClientBirthday = (value: string | null | undefined) => {
  if (!value) return undefined;
  const today = new Date().toISOString().slice(0, 10);
  if (value > today) {
    return "crm.validation.future_birthday";
  }
  return undefined;
};

/**
 * react-hook-form validator for a client's number of children. Optional;
 * when present, must be an integer >= 0.
 */
export const validateChildrenCount = (
  value: number | string | null | undefined,
) => {
  if (value === null || value === undefined || value === "") return undefined;
  const numericValue = Number(value);
  if (!Number.isInteger(numericValue) || numericValue < 0) {
    return "crm.validation.invalid_children_count";
  }
  return undefined;
};

/**
 * react-hook-form validator for a client's marital property regime (Almikia
 * Epic E3b, owner answer A8). Required only when `marital_status` is
 * 'casado'; hidden/optional otherwise. Mirrors the database's
 * `enforce_clients_marital_regime` trigger (TASK-069). Never echoes the
 * entered value.
 */
export const validateClientMaritalRegime = (
  value: string | null | undefined,
  allValues: { marital_status?: MaritalStatus | null },
) => {
  if (allValues.marital_status !== "casado") return undefined;
  if (!value) return "crm.validation.marital_regime_required";
  return undefined;
};

/**
 * Normalizes a client record before it is sent to the data provider: the RFC
 * is uppercased, the CURP is forced to null for persona_moral clients, and
 * the marital property regime is forced to null unless marital status is
 * casado.
 *
 * The CURP reset is deliberate defense-in-depth, not just cosmetics: a client
 * edited from persona_fisica (with a CURP) to persona_moral keeps its old CURP
 * value in the form state even though `validateClientCurp` now rejects it, so
 * this is the last guard before a persona_moral record could otherwise be
 * saved with a CURP. The same defense-in-depth applies to the marital
 * property regime: switching marital status away from casado must never
 * submit a stale regime value.
 */
export const cleanupClientForSave = (
  values: Partial<Client>,
): Partial<Client> => ({
  ...values,
  rfc: values.rfc ? values.rfc.toUpperCase() : values.rfc,
  curp: values.client_type === "persona_moral" ? null : values.curp,
  marital_property_regime:
    values.marital_status === "casado" ? values.marital_property_regime : null,
});
